import { useEffect, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { EventPanelFormType } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useLocationApi } from '../../location-select/hooks/use-location-api';
import { resolveClosestClassifiedLocation } from '../../location-select/helpers/resolve-closest-classified-location';

export type AutoFillLocationStatus = 'idle' | 'resolving' | 'success' | 'failed';

const RESOLVE_TIMEOUT_MS = 10_000;

/**
 * Generic mechanism to resolve a location `query` and write the top classified result
 * straight into the event form's `location` field — once, without opening the picker —
 * while `enabled` is true. The caller owns the policy: when to enable and how the query is
 * derived. (First consumer: the E911 caller auto-import; see `useAutoImportLocationOnE911`.)
 *
 * - Runs at most once per mounted panel: the first time it is enabled with a query it
 *   fills, and never re-resolves for that panel (including after a failure). A newly
 *   opened panel starts fresh.
 * - Skips silently when `query` is `null` (no data to resolve); `status` stays `'idle'`.
 * - Cancel-on-edit: if the user changes the location field while a resolve is in flight,
 *   the fill is abandoned and their input is preserved.
 * - No results / service error / timeout → `status` becomes `'failed'` and the field is
 *   left empty, so the caller can surface a manual-entry message.
 * - `onLocationFilled` runs after a successful write, for consumers that need to react to a
 *   location the form's own watchers cannot see — see `useEventPanelFormPrefill`.
 *
 * Returns the current status so the caller can render a spinner / success / failure indicator.
 *
 * PII/CJI: never logs the query, coordinates, or resolved location.
 */
export function useAutoFillEventLocationField({
    form,
    query,
    enabled,
    onLocationFilled,
}: {
    form: UseFormReturn<EventPanelFormType>;
    query: string | null | undefined;
    enabled: boolean;
    onLocationFilled?: () => void;
}): AutoFillLocationStatus {
    const { multiAgencySubdivisions } = useEnabledFeatures();
    const { searchLocations, resolveEntityLinksForLocation } = useLocationApi(
        multiAgencySubdivisions ? 'MULTI_AGENCY' : 'SINGLE_AGENCY'
    );

    const [status, setStatus] = useState<AutoFillLocationStatus>('idle');

    /*
     * Claimed synchronously before the resolve starts so the one fill survives the effect
     * re-running — `enabled`, `query`, and the location API callbacks all change while a
     * request is in flight, which would otherwise launch duplicate resolves.
     */
    const hasRunRef = useRef(false);
    const resolvingRef = useRef(false);
    const cancelledRef = useRef(false);
    const startLocationRef = useRef<EventPanelFormType['location']>(null);
    const mountedRef = useRef(true);
    useEffect(function trackMounted() {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Cancel-on-edit: if the location value changes to something other than what it was
    // when resolution started — i.e. the user typed/picked — abandon the fill and keep
    // their input. Our own write flips `resolvingRef` off first, so it never trips this.
    const locationValue = useWatch({ control: form.control, name: 'location' });
    useEffect(
        function cancelAutoFillOnUserEdit() {
            if (!resolvingRef.current || locationValue === startLocationRef.current) {
                return;
            }
            cancelledRef.current = true;
            resolvingRef.current = false;
            setStatus('idle');
        },
        [locationValue]
    );

    useEffect(
        function autoFillLocationField() {
            if (hasRunRef.current || !enabled || !query) {
                return;
            }
            hasRunRef.current = true;

            startLocationRef.current = form.getValues('location');
            resolvingRef.current = true;
            cancelledRef.current = false;
            setStatus('resolving');

            let timeoutId: ReturnType<typeof setTimeout>;
            const timeout = new Promise<never>((_resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error('timeout')), RESOLVE_TIMEOUT_MS);
            });
            Promise.race([
                resolveClosestClassifiedLocation(
                    query,
                    { searchLocations, resolveEntityLinksForLocation },
                    // We commit without the calltaker confirming, so "use what I typed" is
                    // not a match — a no-match has to be reported, not silently filled in.
                    { excludeForcedResults: true }
                ),
                timeout,
            ])
                .then((resolved) => {
                    if (!mountedRef.current || cancelledRef.current || !resolvingRef.current) {
                        return;
                    }
                    resolvingRef.current = false;
                    // No matching records → leave the field empty, show the manual-entry message.
                    if (!resolved) {
                        setStatus('failed');
                        return;
                    }
                    form.setValue('location', resolved, {
                        shouldValidate: true,
                        shouldDirty: true,
                    });
                    onLocationFilled?.();
                    setStatus('success');
                })
                .catch(() => {
                    if (!mountedRef.current || cancelledRef.current || !resolvingRef.current) {
                        return;
                    }
                    resolvingRef.current = false;
                    setStatus('failed');
                })
                .finally(() => clearTimeout(timeoutId));
        },
        [enabled, query, searchLocations, resolveEntityLinksForLocation, form, onLocationFilled]
    );

    return status;
}
